import { createTypeAsyncAction } from '@gu-corp/redux-async-lib';
import { ERC721TransferRestricted__factory } from '@gu-corp/erc721-transfer-restricted';
import { ethers } from 'ethers';
import { getEnv } from '~/env';
import { authAPIService } from '~/services/api/auth.service';
import { userAPIService } from '~/services/api/user.service';
import { fetchEcosystemApps } from '~/utils/ecosystem.util';
import { AssetToken, fetchAssetTokens, fetchNetworks } from '~/utils/joc-resource.util';
import * as Sentry from '@sentry/nextjs';
import { LocalStorageKey } from '~/constants/local-storage-key';
import { load } from '~/utils/local.storage.utils';
import { buildTokenId } from '~/utils/string.utils';

// Cache for balance data to avoid excessive RPC calls
const balanceCache: {
  data: {
    balance: bigint;
    testnetBalance: bigint;
    usdtxBalance: bigint;
    usdtxDecimals: number;
    tokenId?: string;
  } | null;
  address: string | null;
  timestamp: number;
} = {
  data: null,
  address: null,
  timestamp: 0,
};

const BALANCE_CACHE_TTL = 10000; // 10 seconds

export const getAccountAction = createTypeAsyncAction('GET_ACCOUNT', async () => {
  const { data } = await userAPIService.getAccount();
  const now = Date.now();

  // Use cache if valid
  if (
    balanceCache.data &&
    balanceCache.address === data.ethAddress &&
    now - balanceCache.timestamp < BALANCE_CACHE_TTL
  ) {
    return {
      ...data,
      ...balanceCache.data,
    };
  }

  const provider = new ethers.JsonRpcProvider(getEnv('NEXT_PUBLIC_JOC_URL') || '');
  const testnetProvider = new ethers.JsonRpcProvider(getEnv('NEXT_PUBLIC_JOC_TESTNET_URL') || '');

  // Prepare USDTX contract
  const usdtxContractAddress = getEnv('NEXT_PUBLIC_USDTX_CONTRACT_ADDRESS');
  const usdtxContract = usdtxContractAddress
    ? new ethers.Contract(
        usdtxContractAddress,
        [
          'function decimals() public view returns (uint8)',
          'function balanceOf(address account) public view returns (uint256)',
        ],
        provider,
      )
    : null;

  // Prepare ERC721 contract
  const tokenContract = ERC721TransferRestricted__factory.connect(
    getEnv('NEXT_PUBLIC_TOKEN_ADDRESS') || '',
    provider,
  );

  // Execute all RPC calls in parallel
  const [balance, testnetBalance, usdtxResult, tokenId] = await Promise.all([
    provider.getBalance(data.ethAddress),
    testnetProvider.getBalance(data.ethAddress),
    usdtxContract
      ? Promise.all([
          usdtxContract.balanceOf(data.ethAddress).catch(() => BigInt(0)),
          usdtxContract.decimals().catch(() => 18),
        ])
      : Promise.resolve([BigInt(0), 18] as const),
    tokenContract.tokenOfOwnerByIndex(data.ethAddress, 0).catch(() => undefined),
  ]);

  const [usdtxBalance, usdtxDecimals] = usdtxResult;

  // Update cache
  balanceCache.data = {
    balance,
    testnetBalance,
    usdtxBalance: BigInt(usdtxBalance.toString()),
    usdtxDecimals: Number(usdtxDecimals),
    tokenId: tokenId?.toString(),
  };
  balanceCache.address = data.ethAddress;
  balanceCache.timestamp = now;

  return {
    ...data,
    balance,
    testnetBalance,
    tokenId: tokenId?.toString(),
    usdtxBalance: BigInt(usdtxBalance.toString()),
    usdtxDecimals: Number(usdtxDecimals),
  };
});

export const getEcosystemAppsAction = createTypeAsyncAction('GET_ECOSYSTEM_APPS', async () => {
  const ecosystemApps = await fetchEcosystemApps();
  return ecosystemApps;
});

export const getAssetTokensAction = createTypeAsyncAction(
  'GET_ASSET_TOKENS',
  async (userAddress: string) => {
    const tokens = await fetchAssetTokens();
    const networks = await fetchNetworks();
    const updatedTokens: Array<
      AssetToken & {
        lastSeenAt?: string;
      }
    > = [];

    for (const token of tokens) {
      try {
        const tokens = load(LocalStorageKey.ASSET_TOKENS);
        const cachedTokens = tokens !== null ? JSON.parse(tokens) : [];

        const cachedToken = cachedTokens.find(
          (cachedAssetToken: { id: string; symbol: string; lastSeenAt: Record<string, string> }) =>
            cachedAssetToken.id === buildTokenId(token.symbol, token.chainId, token.address),
        );
        const lastSeenAt =
          cachedToken && cachedToken.lastSeenAt && cachedToken.lastSeenAt[userAddress]
            ? cachedToken.lastSeenAt[userAddress]
            : new Date().toISOString();

        updatedTokens.push({
          ...token,
          lastSeenAt,
        });
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        continue;
      }
    }

    return { updatedTokens, networks };
  },
);

export const logoutAction = createTypeAsyncAction('LOGOUT_ACCOUNT', async () => {
  const { data } = await authAPIService.logout();
  return data;
});
